import {
  CalendarClock,
  CreditCard as CardIcon,
  FileText,
  Plus,
  RefreshCcw,
  Settings2,
  ShoppingCart,
  SlidersHorizontal,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Section } from '@/components/common/Blocks'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDate, fmtMoney } from '../../../shared/format'
import {
  CREDIT_SETTINGS_PERM,
  STATUS_TONE,
  useCreditCards,
  useIssueDueStatements,
  type CardScope,
  type CreditCard,
} from '../../credit'
import { toastError } from '../../ui'
import { BankLogo } from '../banks/BankLogo'
import { CardForm } from './CardForm'
import { CardStatementsModal } from './CardStatementsModal'
import { CardTxnForm } from './CardTxnForm'
import { CreditSettingsModal } from './CreditSettingsModal'
import { ReminderSettingsButton } from './ReminderSettings'

type Props = { scope: CardScope; canWrite: boolean }

/**
 * Kredi kartı ekstreleri: son ekstre (dönem borcu, asgari, son ödeme, durum), sonraki kesim, ekstre geçmişi,
 * taksitli harcama ve kartın oranları. Şirket kartları "Kartlar" hesaplarından gelir; kişisel kartları kişi ekler.
 */
export function CreditCardsSection({ scope, canWrite }: Props) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const cards = useCreditCards(scope)
  const issue = useIssueDueStatements()
  const [form, setForm] = useState<{ card: CreditCard | null } | null>(null)
  const [statementsOf, setStatementsOf] = useState<CreditCard | null>(null)
  const [txnFor, setTxnFor] = useState<CreditCard | null>(null)
  const [settings, setSettings] = useState(false)
  const personal = scope === 'personal'

  return (
    <Section
      title={t(personal ? 'finance.credit.myCards' : 'finance.credit.title')}
      icon={CardIcon}
      bodyClassName="p-0"
      actions={
        <div className="flex flex-wrap gap-2">
          {!personal ? <ReminderSettingsButton scope="company" /> : null}
          {can(CREDIT_SETTINGS_PERM) ? (
            <Button size="sm" variant="ghost" onClick={() => setSettings(true)}>
              <Settings2 />
              {t('finance.credit.settings')}
            </Button>
          ) : null}
          {!personal && canWrite ? (
            <Button
              size="sm"
              variant="outline"
              disabled={issue.isPending}
              onClick={() =>
                issue.mutate(undefined, {
                  onSuccess: (r) =>
                    toast.success(
                      t('finance.credit.issued', { count: Number((r as Record<string, unknown>)?.issued ?? 0) }),
                    ),
                  onError: toastError,
                })
              }
            >
              <RefreshCcw />
              {t('finance.credit.issueDue')}
            </Button>
          ) : null}
          {personal && canWrite ? (
            <Button size="sm" onClick={() => setForm({ card: null })}>
              <Plus />
              {t('finance.credit.addCard')}
            </Button>
          ) : null}
        </div>
      }
    >
      {cards.isError ? (
        <ErrorState error={cards.error} onRetry={() => void cards.refetch()} />
      ) : cards.isLoading ? (
        <Skeleton className="m-4 h-32" />
      ) : !cards.data?.length ? (
        <EmptyState icon={CardIcon} title={t(personal ? 'finance.credit.emptyPersonal' : 'finance.credit.empty')} />
      ) : (
        <ul className="divide-y">
          {cards.data.map((c) => {
            const st = c.lastStatement
            return (
              <li key={c.id || c.moneyAccountId} className="flex flex-wrap items-start gap-x-4 gap-y-3 px-4 py-3">
                <div className="min-w-0 flex-1 basis-56 space-y-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    <BankLogo url={c.bankLogoUrl} name={c.bankName} />
                    {c.name}
                    {c.last4 ? <span className="text-muted-foreground font-mono text-xs">•••• {c.last4}</span> : null}
                    {!c.active ? <Badge tone="neutral">{t('finance.credit.inactive')}</Badge> : null}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {c.bankName ? `${c.bankName} · ` : ''}
                    {t('finance.credit.limit')}: {fmtMoney(c.limit)}
                    {c.ready ? ` · ${t('finance.credit.days', { statement: c.statementDay, due: c.dueDay })}` : ''}
                  </p>
                  {!c.ready ? <p className="text-warning text-xs">{t('finance.credit.notReady')}</p> : null}
                  {c.nextStatementDate ? (
                    <p className="text-muted-foreground flex items-center gap-1 text-xs">
                      <CalendarClock className="size-3.5" aria-hidden />
                      {t('finance.credit.nextStatement', { date: fmtDate(c.nextStatementDate) })}
                    </p>
                  ) : null}
                </div>
                {st ? (
                  <dl className="grid min-w-[16rem] grid-cols-3 gap-x-4 gap-y-1 text-xs">
                    <dt className="text-muted-foreground">{t('finance.credit.totalDue')}</dt>
                    <dt className="text-muted-foreground">{t('finance.credit.minDue')}</dt>
                    <dt className="text-muted-foreground">{t('finance.credit.dueDate')}</dt>
                    <dd className="text-sm font-semibold tabular-nums">{fmtMoney(st.totalDue)}</dd>
                    <dd className="text-sm tabular-nums">{fmtMoney(st.minDue)}</dd>
                    <dd className="text-sm">{fmtDate(st.dueDate)}</dd>
                    <dd className="col-span-3 pt-1">
                      <Badge tone={STATUS_TONE[st.status]}>{t(`finance.credit.status.${st.status}`)}</Badge>
                      <span className="text-muted-foreground ml-2">
                        {t('finance.credit.paidSince', { amount: fmtMoney(st.paidSince) })}
                      </span>
                    </dd>
                  </dl>
                ) : (
                  <p className="text-muted-foreground self-center text-xs">{t('finance.credit.noStatement')}</p>
                )}
                <div className="flex flex-wrap gap-2">
                  {c.id ? (
                    <Button size="sm" variant="outline" onClick={() => setStatementsOf(c)}>
                      <FileText />
                      {t('finance.credit.statements')}
                    </Button>
                  ) : null}
                  {canWrite && c.id && c.ready ? (
                    <Button size="sm" variant="outline" onClick={() => setTxnFor(c)}>
                      <ShoppingCart />
                      {t(personal ? 'finance.credit.addTxn' : 'finance.credit.addInstallment')}
                    </Button>
                  ) : null}
                  {canWrite ? (
                    <Button size="sm" variant="ghost" onClick={() => setForm({ card: c })}>
                      <SlidersHorizontal />
                      {t(personal ? 'common.edit' : 'finance.credit.profile')}
                    </Button>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ul>
      )}
      {form ? <CardForm scope={scope} card={form.card} onDone={() => setForm(null)} /> : null}
      {statementsOf ? (
        <CardStatementsModal card={statementsOf} canWrite={canWrite} onClose={() => setStatementsOf(null)} />
      ) : null}
      {txnFor ? <CardTxnForm card={txnFor} onDone={() => setTxnFor(null)} /> : null}
      {settings ? <CreditSettingsModal onClose={() => setSettings(false)} /> : null}
    </Section>
  )
}
